<template>
  <transition-group tag="div" class="storefinderAccordion" name="storefinder-fade">
    <StorefinderCollapse v-for="(parent, i) in pageTree" :key="parent.page.uid" v-show="selectedRootPages.includes(parent.page.uid)" :open="selectedRootPages.includes(parent.page.uid)" ref="panel">
      <span slot="title" class="headline--2">{{ parent.page.title }}</span>
      <div class="storefinderAccordion__categoryList">
        <fieldset v-for="child in parent.children">

          <!-- Category checkbox for filter -->
          <label class="storefinderAccordion__label">
            <!-- NOTICE: Checkbox value is category UID (used in filter) -->
            <input type="checkbox" v-if="tag(child)" :value="tag(child).uid" class="storefinderAccordion__checkbox" v-model="selectedTags" @change="onTagCheckboxChange">
            <span class="storefinderAccordion__category">{{ child.page.title }}</span>
            <a :href="child.page.slug" class="storefinderAccordion__link" target="_blank">&rarr;</a>
          </label>

          <!-- Bookmark checkboxes -->
          <ul>
            <li v-for="grandChild in child.children" class="storefinderAccordion__bookmarkItem">
              <label class="storefinderAccordion__label">
                <!-- NOTICE: Checkbox value is page UID (only for bookmarks/pages, not used in filter) -->
                <input type="checkbox" :value="grandChild.page.uid" class="storefinderAccordion__checkbox" @change="onBookmarkCheckboxChange($event, child)" v-model="selectedPages">
                <span>{{ grandChild.page.title }}</span>
                <a :href="grandChild.page.slug" class="storefinderAccordion__link" target="_blank">&rarr;</a>
              </label>
            </li>
          </ul>
        </fieldset>
      </div>
    </StorefinderCollapse>
  </transition-group>
</template>

<script>
  import {mapGetters} from 'vuex'

  export default {
    name: 'storefinder-accordion',
    data() {
      return {
        openPanels: [],
      };
    },

    props: {
      pageTree: {
        type: Array,
        required: true,
      },
      submitButtonText: {
        type: String,
        default: 'Submit',
      },
    },

    computed: {
      ...mapGetters(['selectedRootPages']),
      selectedTags: {
        get() {
          return this.$store.getters.selectedTags;
        },
        set(value) {
          this.$store.commit('updateSelectedTags', value);
        },
      },
      selectedPages: {
        get() {
          return this.$store.getters.selectedPages;
        },
        set(value) {
          this.$store.commit('updateSelectedPages', value);
        },
      },
    },

    methods: {
      onPanelHeaderClick(page) {
        this.togglePanel(page);
      },
      tag(pageData) {
        return pageData.page.storefinder_tag || null;
      },
      onTagCheckboxChange(event) {
        if (event.target.checked) {
          return;
        }

        // Deselect child pages
        this.pageTree.forEach((rootPage) => {
          Object.values(rootPage.children).forEach((parent) => {
            const tagUid = parent.page.storefinder_tag ? parent.page.storefinder_tag.uid : 0;

            if (tagUid !== parseInt(event.target.value)) {
              return;
            }

            parent.children.forEach((child) => {
              this.$store.commit('deselectPage', child.page.uid);
            });
          });
        });
      },
      onBookmarkCheckboxChange(event, parentPage) {
        if (!parentPage.page.storefinder_tag) {
          return;
        }

        if (event.target.checked) {
          // Parent page tag must be selected.
          this.$store.commit('selectTag', parentPage.page.storefinder_tag.uid);
        } else {
          // Parent page tag must be deselected if none of the subpages are selected.
          let isSomethingChecked = parentPage.children.some(page => this.selectedPages.includes(page.page.uid));
          if (!isSomethingChecked) {
            this.$store.commit('deselectTag', parentPage.page.storefinder_tag.uid);
          }
        }
      },
      loadBookmarks() {
        this.$store.commit('clearPages');
        this.pageTree.forEach(rootPage => {
          Object.values(rootPage.children).forEach(parent => {
            const parentUid = parseInt(parent.page.uid);
            const isParentBookmarked = this.$store.getters['bookmarks'].includes(parentUid);

            if (isParentBookmarked) {
              this.$store.commit('selectPage', parentUid);
            }

            // Also search children and grandchildren.
            parent.children.some(child => {
              const childUid = parseInt(child.page.uid);
              const isChildBookmarked = this.$store.getters['bookmarks'].includes(childUid)
                || Object.values(child.children).some(grandchild => {
                  const grandchildUid = parseInt(grandchild.page.uid);

                  return this.$store.getters.bookmarks.includes(grandchildUid);
                });

              if (isChildBookmarked) {
                this.$store.commit('selectPage', childUid);

                if (parent.page.storefinder_tag) {
                  // Parent page category must be selected so that the panel body can expand.
                  this.$store.commit('selectTag', parent.page.storefinder_tag.uid);
                }

                if (rootPage.page.storefinder_tag) {
                  // Root page category must be selected so that the panel body can expand.
                  this.$store.commit('selectTag', rootPage.page.storefinder_tag.uid);
                }
              }
            });
          });
        });
      },
      preselectPage() {
        const preselect = this.$store.getters.preselectPage;

        if (!this.$store.getters.preselectPage) {
          return;
        }

        this.pageTree.forEach(rootPage => {
          Object.values(rootPage.children).forEach(parent => {
            const parentUid = parseInt(parent.page.uid);
            const isParentBookmarked = parentUid === preselect;

            if (isParentBookmarked) {
              this.$store.commit('selectPage', parentUid);
            }

            // Also search children and grandchildren.
            parent.children.some(child => {
              const childUid = parseInt(child.page.uid);
              const isChildBookmarked = childUid === preselect
                || Object.values(child.children).some(grandchild => {
                  const grandchildUid = parseInt(grandchild.page.uid);

                  return grandchildUid === preselect;
                });

              if (isChildBookmarked) {
                this.$store.commit('selectPage', childUid);

                if (parent.page.storefinder_tag) {
                  this.$store.commit('selectTag', parent.page.storefinder_tag.uid);
                }

                // Root page category must be selected so that the panel body can expand.
                this.$store.commit('selectRootPage', rootPage.page.uid);
              }
            });
          });
        });
      },
      accordionPanelClass(page) {
        return {
          'storefinderAccordion__panel--visible': this.isPanelVisible(page),
          'storefinderAccordion__panel--open': this.isPanelOpen(page),
        };
      },
      isPanelOpen(page) {
        return this.openPanels.includes(page.uid);
      },
      isPanelVisible(page) {
        return this.$store.getters.selectedRootPages.includes(page.uid);
      },
      closePanel(page) {
        this.openPanels = this.openPanels.filter(panel => panel !== page.uid);
      },
      openPanel(page) {
        this.openPanels.push(page.uid);
      },
      togglePanel(page) {
        if (this.isPanelOpen(page)) {
          this.closePanel(page);
        } else {
          this.openPanel(page);
        }
      },
      panelStyle(i, page) {
        if (!this.$refs.panel) {
          return {};
        }

        const panel = this.$refs.panel[i];

        return {
          height: this.isPanelVisible(page) ? `auto` : 0,
        };
      },
      panelBodyStyle(i, page) {
        if (!this.$refs.panelBody) {
          return {};
        }

        const panelBody = this.$refs.panelBody[i];

        return {
          height: this.isPanelOpen(page) ? `auto` : 0,
        };
      },
      openPanelsWithSelections() {
        this.pageTree.forEach((rootPage) => {
          const isSelected = this.$store.getters.selectedRootPages.includes(rootPage.page.uid);

          if (isSelected) {
            this.openPanel(rootPage.page);
          } else {
            this.closePanel(rootPage.page);
          }
        });
      },
    },
  };
</script>
